import React, { useState, useEffect, useMemo } from 'react';
import {
  TextField, FormControl, InputLabel, Select, MenuItem, Button, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Box, ListSubheader, InputAdornment
} from '@mui/material';
import axios from 'axios';
import { useAuth } from '../../AuthContext';
import SearchIcon from "@mui/icons-material/Search";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const NewDamageForm = ({ isOpen, onClose, selectedRowData }) => {
  const today = new Date().toISOString().split('T')[0];
  const { userRole } = useAuth();
  const [formData, setFormData] = useState({
    selectedItem: '',
    selectedQuantity: '',
    selectedDate: today,
    addToQuantity: '0',
  });
  const [damageItems, setDamageItems] = useState([]);
  const [allItemsData, setAllItemsData] = useState([]);
  const [employee, setEmployee] = useState(true);

  useEffect(() => {
    setEmployee(userRole !== "manager");
  }, [userRole]);

  useEffect(() => {
    const fetchAllItems = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BASE_URL}/itemservices/all_itemservices`);
        const responseData = await response.json();
        if (responseData) {
          const itemData = responseData.itemservice.filter(item => item.type === "Item");
          setAllItemsData(itemData);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    }
    fetchAllItems();

    if (selectedRowData) {
      setFormData({
        selectedItem: selectedRowData.damageitem,
        selectedQuantity: selectedRowData.damagequantity,
        selectedDate: selectedRowData.damagedate,
        addToQuantity: '0',
      });
    } else {
      setFormData({
        selectedItem: '',
        selectedQuantity: '',
        selectedDate: today,
        addToQuantity: '0',
      });
    }
  }, [selectedRowData, today]);

  const getPrice = (item) => {
    const selected = allItemsData.find(data => data.itemservice === item);
    return selected ? selected.rate : 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (selectedRowData) {
      await updateData();
    } else {
      const newItem = { ...formData };
      const selectedItemId = allItemsData.find(item => item.itemservice === newItem.selectedItem)?.id;
      newItem.itemId = selectedItemId;
      setDamageItems([...damageItems, newItem]);
      setFormData({
        selectedItem: '',
        selectedQuantity: '',
        selectedDate: today,
        addToQuantity: '0',
      });
      await postData();
    }
    onClose();
    setDamageItems([]);
  };

  const handleAddItem = () => {
    const selectedItemId = allItemsData.find(item => item.itemservice === formData.selectedItem)?.id;
    const newItem = {
      ...formData,
      itemId: selectedItemId,
      selectedItemId,
    };
    setDamageItems([...damageItems, newItem]);
    setFormData({
      selectedItem: '',
      selectedQuantity: '',
      selectedDate: today,
      addToQuantity: '0',
    });
  };

  const postData = async () => {
    try {
      const dataList = damageItems.map(item => ({
        itemId: item.selectedItemId,
        damageitem: item.selectedItem,
        damagequantity: item.selectedQuantity,
        damageamount: item.selectedQuantity * getPrice(item.selectedItem),
        damagedate: item.selectedDate
      }));

      const response = await axios.post(`${process.env.REACT_APP_BASE_URL}/damages/add_damage`, dataList);

      if (response.status === 200) {
        toast.success(`Damage created successfully.`)
        onClose();
      }
    } catch (error) {
      toast.error("Error posting data.");
    }
  };

  const updateData = async () => {
    if (!formData.selectedItem || !formData.selectedQuantity) {
      console.log("EMPTY FIELD");
    } else {
      const response = await axios.put(`${process.env.REACT_APP_BASE_URL}/damages/edit_damage`, {
        "id": selectedRowData.id,
        "damageitem": formData.selectedItem,
        "damagequantity": parseInt(formData.selectedQuantity) + parseInt(formData.addToQuantity),
        "damageamount": formData.selectedQuantity * getPrice(formData.selectedItem),
        "damagedate": formData.selectedDate
      });
      if (response.status === 200) {
        toast.success(`Damage updated successfully.`)
      }
    }
  };

  const handleRemoveItem = (index) => {
    const updatedItems = [...damageItems];
    updatedItems.splice(index, 1);
    setDamageItems(updatedItems);
  };


  const containsText = (text, searchText) =>
    text.toLowerCase().indexOf(searchText.toLowerCase()) > -1;

  const [searchText, setSearchText] = useState("");
  
  const displayedOptions = useMemo(
    () =>
      searchText
        ? allItemsData.filter(
          (item) =>
            containsText(item.itemservice, searchText) ||
            containsText(item.id.toString(), searchText) || containsText(item.type, searchText)
        )
        : allItemsData,
    [allItemsData, searchText]
  );

  return (
    <div style={{
      display: isOpen ? 'block' : 'none',
      position: 'fixed',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      backgroundColor: 'rgba(255, 255, 255, 0.9)',
      padding: '20px',
      boxShadow: '0px 0px 10px rgba(0, 0, 0, 0.2)',
      zIndex: '999',
      backdropFilter: 'blur(5px)',
    }}>
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop={false} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />
      <h2 className='text-2xl font-bold font-poppins ml-2 mt-3 mb-3'>{selectedRowData ? "Update Damaged Item" : "Add New Damaged Item(s)"}</h2>
      <form onSubmit={handleSubmit}>

        <Box sx={{}}>
          <FormControl fullWidth>
            <InputLabel id="item">Items</InputLabel>
            <Select
              labelId="item"
              id="item"
              value={formData.selectedItem}
              label="Items"
              onChange={(e, item) => {
                setFormData({ ...formData, selectedItem: e.target.value })
              }}
              sx={{ mb: 4 }}
            >
              <MenuItem value="" disabled>
                Select an Item
              </MenuItem>
              <ListSubheader>
                <TextField
                  size="small"
                  autoFocus
                  placeholder="Type to search..."
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    )
                  }}
                  onChange={(e) => setSearchText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key !== "Escape") {
                      e.stopPropagation();
                    }
                  }}
                />
              </ListSubheader>
              <ListSubheader sx={{ fontSize: 18, color: "grey", borderBottom: 1, borderTop: 1, fontStyle: "italic" }}>Items</ListSubheader>
              {displayedOptions.filter(item => item.type === 'Item').map((item, index) => (
                <MenuItem key={index} id={item.id} value={item.itemservice}>
                  {`${item.itemservice}`}<span className=" ml-2 text-[12px] italic text-gray-500">ID: {item.id}</span>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>


        <TextField
          type="text"
          variant="outlined"
          color="secondary"
          label="Quantity"
          onChange={(e) => setFormData({ ...formData, selectedQuantity: e.target.value })}
          value={formData.selectedQuantity}
          fullWidth
          sx={{ mb: 4 }}
          disabled={selectedRowData && employee}
        />

        <TextField
          type="date"
          variant="outlined"
          color="secondary"
          label=""
          onChange={(e) => setFormData({ ...formData, selectedDate: e.target.value })}
          value={formData.selectedDate}
          fullWidth
          required
          sx={{ mb: 4 }}
          disabled={selectedRowData && employee}
        />

        {selectedRowData && employee && (
          <TextField
            type="number"
            variant="outlined"
            color="secondary"
            label="Add Quantity"
            onChange={(e) => setFormData({ ...formData, addToQuantity: e.target.value })}
            value={formData.addToQuantity}
            fullWidth
            sx={{ mb: 4 }}
          />
        )}

        {!selectedRowData && (
          <Button
            type="button"
            variant="contained"
            color="primary"
            size="large"
            onClick={handleAddItem}
            sx={{ mb: 2 }}
          >
            Add Item
          </Button>
        )}

        {!selectedRowData && (
          <>
            <TableContainer component={Paper} sx={{ mb: 2, maxHeight: 150, minHeight: 100 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Item</TableCell>
                    <TableCell>Quantity</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {damageItems.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell>{item.selectedItem}</TableCell>
                      <TableCell>{item.selectedQuantity}</TableCell>
                      <TableCell>{item.selectedDate}</TableCell>
                      <TableCell>
                        <Button onClick={() => handleRemoveItem(index)} variant="outlined" color="error">Remove</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

        <Button
          type="submit"
          variant="contained"
          color="primary"
          size="large"
          sx={{ mb: 2 }}
        >
          {selectedRowData ? "Update Item/Service" : "Add Item/Service"}
        </Button>
      </form>
      <Button
        onClick={onClose}
        variant="outlined"
        color="error"
        size="large"
        sx={{ mb: 2 }}
      >
        Close
      </Button>
    </div>
  );
};

export default NewDamageForm;